'use client'

import React from 'react'

type Props = {
    angleDeg: number
}

const WIDTH = 340
const HEIGHT = 200
const BASE_Y = 170
const BASE_X = 30
const RAMP_LENGTH = 220

export default function InclineDiagramChart({ angleDeg }: Props) {
    const angle = Math.min(Math.max(angleDeg, 1), 89)
    const rad = (angle * Math.PI) / 180

    const topX = BASE_X + RAMP_LENGTH * Math.cos(rad)
    const topY = BASE_Y - RAMP_LENGTH * Math.sin(rad)

    // block sits partway up the ramp
    const blockT = 0.55
    const blockX = BASE_X + RAMP_LENGTH * blockT * Math.cos(rad)
    const blockY = BASE_Y - RAMP_LENGTH * blockT * Math.sin(rad)

    const weightLen = 55
    const normalLen = 45
    const parallelLen = 45

    // weight vector: straight down
    const weightEndX = blockX
    const weightEndY = blockY + weightLen

    // normal vector: perpendicular to incline surface, pointing away from surface
    const normalAngle = rad + Math.PI / 2
    const normalEndX = blockX + normalLen * Math.cos(normalAngle)
    const normalEndY = blockY - normalLen * Math.sin(normalAngle)

    // parallel (sliding) component: along the incline surface, pointing downhill
    const parallelEndX = blockX - parallelLen * Math.cos(rad)
    const parallelEndY = blockY + parallelLen * Math.sin(rad)

    const arrow = (x1: number, y1: number, x2: number, y2: number, color: string) => {
        const angle2 = Math.atan2(y2 - y1, x2 - x1)
        const headLen = 7
        const h1x = x2 - headLen * Math.cos(angle2 - Math.PI / 6)
        const h1y = y2 - headLen * Math.sin(angle2 - Math.PI / 6)
        const h2x = x2 - headLen * Math.cos(angle2 + Math.PI / 6)
        const h2y = y2 - headLen * Math.sin(angle2 + Math.PI / 6)
        return (
            <g>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={2} />
                <polygon points={`${x2},${y2} ${h1x},${h1y} ${h2x},${h2y}`} fill={color} />
            </g>
        )
    }

    return (
        <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ maxWidth: '100%', height: 'auto' }}>
            {/* ground */}
            <line x1={10} y1={BASE_Y} x2={WIDTH - 10} y2={BASE_Y} stroke="#9ca3af" strokeWidth={1} />
            {/* incline surface */}
            <line x1={BASE_X} y1={BASE_Y} x2={topX} y2={topY} stroke="#374151" strokeWidth={2.5} />
            {/* angle arc + label */}
            <path d={`M ${BASE_X + 30} ${BASE_Y} A 30 30 0 0 0 ${BASE_X + 30 * Math.cos(rad)} ${BASE_Y - 30 * Math.sin(rad)}`} fill="none" stroke="#9ca3af" strokeWidth={1} />
            <text x={BASE_X + 36} y={BASE_Y - 8} fontSize={10} fill="#6b7280">{angle.toFixed(0)}°</text>
            {/* block */}
            <circle cx={blockX} cy={blockY} r={7} fill="#111827" />
            {/* vectors */}
            {arrow(blockX, blockY, weightEndX, weightEndY, '#dc2626')}
            {arrow(blockX, blockY, normalEndX, normalEndY, '#2563eb')}
            {arrow(blockX, blockY, parallelEndX, parallelEndY, '#16a34a')}
            {/* legend */}
            <text x={weightEndX + 4} y={weightEndY + 4} fontSize={10} fill="#dc2626">W</text>
            <text x={normalEndX + 4} y={normalEndY} fontSize={10} fill="#2563eb">N</text>
            <text x={parallelEndX - 14} y={parallelEndY + 12} fontSize={10} fill="#16a34a">F∥</text>
        </svg>
    )
}
